import { ConfigurationService } from '../../../configuration/services/configuration/configuration.service';
import {
  KnowledgeBase,
  KnowledgeBaseModules,
  KnowledgeBaseSection,
  KnowledgeBaseStore,
  KnowledgeBaseStoreResponse,
  KnowledgeBaseValue,
} from '../../types/knowledge-base.interface';
import { APIHandler } from '../api-handler/api-handler';
import { Injectable } from '@angular/core';
import AutoQueue from 'src/app/shared/classes/auto-queue/auto-queue';
import jiff from 'jiff';
import { MessageService } from 'src/app/modules/message/services/message/message.service';
import { LoggingService } from 'src/app/shared/services/logging/logging.service';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class KnowledgeBaseAPI implements KnowledgeBase {
  user_id = new BehaviorSubject<string | number>(-1);

  authToken = '';
  readonly AUTHORIZED_KB_MODULES = [
    'PowerPath_ActivityCustomizations',
    'MainMenu',
    'HTMLActivityStorage',
    'MicroLessonVideoStates',
  ];
  private getHydrate = `sync/`;
  private patchHydrate = `merge?id=`;
  private putHydrate = `save?id=`;
  private pathBase = `knowledge-base`;
  private kbCache?: KnowledgeBaseStore | null;
  private applicationState?: KnowledgeBaseStore | null; //the last known good state
  private apiHandler: APIHandler;
  private autoQueue = new AutoQueue();
  private inFlightRetrieve: Promise<KnowledgeBaseStoreResponse | null> | null =
    null;

  constructor(
    private configService: ConfigurationService,
    private messageService: MessageService,
    private loggingService: LoggingService
  ) {
    this.apiHandler = new APIHandler(
      {
        baseUrl: this.configService.config.olpApiDomain,
        trailingSlash: 'none',
      },
      this.configService.config.configId
    );
  }

  setCcoVersion(version: number): void {
    if (this.kbCache) {
      this.kbCache.ccoVersion = version;
    }
  }

  setAuthToken(authToken: string): void {
    this.authToken = authToken;
    this.configureHandler();
  }

  configureHandler(): void {
    this.apiHandler.configure({
      baseUrl: this.configService.config.olpApiDomain,
      defaultHeaders: {
        Authorization: 'Bearer ' + this.authToken,
        'Content-Type': 'application/json',
      },
    });
    this.apiHandler.messageService = this.messageService;
    this.apiHandler.loggingService = this.loggingService;
  }

  async getAll(): Promise<KnowledgeBaseModules | null | undefined> {
    let returnValue = null;

    if (!this.kbCache) {
      await this.autoQueue
        .enqueueAQ(() => this.retrieve())
        .catch(error => {
          if (error.status === 404) {
            return this.createCleanKbAndSync();
          } else {
            return Promise.reject(error);
          }
        });
    }
    returnValue = this.kbCache?.modules;
    return returnValue;
  }

  async get(section: string, key: string): Promise<KnowledgeBaseValue> {
    let returnValue = null;

    if (!this.kbCache) {
      await this.autoQueue.enqueueAQ(() => this.retrieve());
    }

    const modules = this.getModules();
    if (modules && modules[section] && modules[section][key]) {
      returnValue = modules[section][key];
    }
    return returnValue;
  }

  async getSection(section: string): Promise<KnowledgeBaseSection> {
    if (!this.kbCache) {
      await this.autoQueue.enqueueAQ(() => this.retrieve());
    }

    const modules = this.getModules();
    if (modules && modules[section]) {
      return modules[section];
    }
    throw new Error(`Section not found`);
  }

  set(section: string, key: string, value: KnowledgeBaseValue): void {
    const modules = this.getModules() || {};
    if (Object.prototype.hasOwnProperty.call(modules, section)) {
      modules[section][key] = value;
    } else {
      modules[section] = { [key]: value };
    }
    const ccoVersion = this.kbCache?.ccoVersion ?? 0;

    this.kbCache = {
      ccoVersion,
      modules,
      removeSet: [],
    };
  }

  createCleanKbAndSync(): Promise<unknown> {
    this.kbCache = {
      ccoVersion: 0,
      modules: {},
      removeSet: [],
    };
    const path = this.buildPath(this.putHydrate);
    return this.autoQueue.enqueueAQ(() =>
      this.apiHandler
        .put<KnowledgeBaseStoreResponse>(
          path,
          this.kbCache as KnowledgeBaseStore
        )
        .then(res => {
          if (this.kbCache && res) {
            this.kbCache.ccoVersion = res.data.ccoVersion;
          }
        })
        .catch(async (err: Response) => {
          console.error(`couldn't create a clean kb`, err);
          throw err;
        })
    );
  }

  /**
   * DANGER ZONE
   * This will delete your entire kb if you sync after calling this, use with caution
   */
  deleteAll(): void {
    if (!this.kbCache) return;
    const keys = Object.keys(this.kbCache?.modules);
    const ccoVersion = this.kbCache?.ccoVersion ?? 0;
    this.kbCache = {
      ccoVersion,
      modules: {},
      removeSet: keys,
    };
  }

  delete(section: string, key: string): void {
    const modules = this.getModules() || {};
    if (Object.prototype.hasOwnProperty.call(modules, section)) {
      delete modules[section][key];
    }
    const ccoVersion = this.kbCache?.ccoVersion ?? 0;
    this.kbCache = {
      ccoVersion,
      modules,
      removeSet: [],
    };
  }

  async sync(): Promise<void> {
    if (!this.kbCache) {
      return Promise.reject('no kb cache to sync');
    }

    const path = this.buildPath(this.patchHydrate);
    await this.autoQueue
      .enqueueAQ(() =>
        this.apiHandler.patch<KnowledgeBaseStoreResponse>(
          path,
          this.kbCache as KnowledgeBaseStore
        )
      )
      .then(res => {
        //res is actually of type PatchResponse
        if (this.kbCache && res) {
          this.kbCache.ccoVersion = res.data.ccoVersion;
          this.applicationState = structuredClone(this.kbCache);
        }
      })
      .catch(async (err: Response) => {
        if (err.status === 409) {
          const resolved = await this.resolveCcoConflict();
          if (!resolved) {
            throw err;
          }
        } else {
          throw err;
        }
      });
  }

  async resolveCcoConflict(): Promise<boolean> {
    const checkpointState = structuredClone(this.kbCache);
    const conflictedStateResponse = await this.autoQueue.enqueueAQ(() =>
      this.retrieve(true)
    );
    const applicationState = structuredClone(this.applicationState);
    if (!conflictedStateResponse || !applicationState || !checkpointState) {
      return false;
    }
    const conflictedState = this.getFilteredKBCacheResponse({
      ccoVersion: conflictedStateResponse.data.ccoVersion,
      modules: conflictedStateResponse.data.modules,
    });

    const remotePatch = jiff.diff(conflictedState, checkpointState);
    const localPatch = jiff.diff(applicationState, checkpointState);
    let areSame = false;

    if (remotePatch.length > 0) {
      areSame = remotePatch.every((patch, i) => {
        return JSON.stringify(patch) === JSON.stringify(localPatch[i]);
      });
    } else {
      areSame = true;
    }

    if (!areSame) {
      return false;
    }

    const mergeState = jiff.patch(
      localPatch,
      conflictedState
    ) as KnowledgeBaseStore;
    if (mergeState) {
      this.kbCache = mergeState;
      this.kbCache.ccoVersion = conflictedState.ccoVersion;
      const path = this.buildPath(this.putHydrate);
      if (this.kbCache) {
        await this.autoQueue
          .enqueueAQ(() =>
            this.apiHandler.patch(path, this.kbCache as KnowledgeBaseStore)
          )
          .then(res => {
            if (this.kbCache && res) {
              this.kbCache.ccoVersion = res.data.ccoVersion;
              this.applicationState = structuredClone(this.kbCache);
              console.log('successfully updated after merge', res);
            }
          })
          .catch((res: Response) => {
            console.error('failed to patch after merge', res);
          });
      }
    } else {
      console.log('we dont have merge state and mergeState data', mergeState);
    }
    return true;
  }

  async setAndSync(
    section: string,
    key: string,
    value: KnowledgeBaseValue
  ): Promise<void> {
    this.set(section, key, value);
    return this.sync();
  }

  async retrieve(
    forceRefresh?: boolean
  ): Promise<KnowledgeBaseStoreResponse | null> {
    if (this.inFlightRetrieve === null) {
      if (!forceRefresh && this.kbCache) {
        return null;
      }
      const path = this.buildPath();
      this.inFlightRetrieve =
        this.apiHandler.get<KnowledgeBaseStoreResponse>(path);
      this.inFlightRetrieve.then(kb => {
        if (kb !== null) {
          this.kbCache = this.getFilteredKBCacheResponse({
            ccoVersion: kb.data.ccoVersion,
            modules: kb.data.modules,
          });
          this.applicationState = structuredClone(this.kbCache);
        }
        this.inFlightRetrieve = null;
      });
    }

    return this.inFlightRetrieve;
  }

  private getModules(): KnowledgeBaseModules | undefined {
    return this.kbCache?.modules;
  }

  private buildPath(pathHydrate: string = ''): string {
    return `${this.pathBase}/${pathHydrate}${this.user_id.getValue()}`;
  }

  private getFilteredKBCacheResponse(
    kb: KnowledgeBaseStore
  ): KnowledgeBaseStore {
    const kbCache: KnowledgeBaseStore = {
      ccoVersion: kb.ccoVersion,
      modules: Object.fromEntries(
        this.AUTHORIZED_KB_MODULES.filter(mod => kb.modules[mod]).map(prop => [
          prop,
          kb.modules[prop],
        ])
      ) as KnowledgeBaseModules,
    };

    return kbCache;
  }
}
